"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { AnnouncementBanner } from "@/lib/types";

const STORAGE_KEY = "hy-banner-dismissed";

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    // Private-browsing/blocked storage -- just show the banner every load, not a failure.
    return false;
  }
}

/** Site-wide announcement banner, admin-editable from /admin/content -- see AdminContent.tsx.
 * A single doc read on mount, not per-request server rendering, matching how every other public
 * page in this app already reads Firestore client-side (see the performance/caching note left
 * for the client -- full server-side ISR is a follow-up, not done here). */
export default function AnnouncementBannerDisplay() {
  const pathname = usePathname();
  const [banner, setBanner] = useState<AnnouncementBanner | null>(null);
  // Lazy initializer, not an effect -- reading sessionStorage here runs once, synchronously,
  // as part of computing the initial state itself, which is exactly what useState's lazy form
  // is for; it's not a side effect smuggled into render the way calling setState from an effect
  // body would be.
  const [dismissed, setDismissed] = useState(readDismissed);
  // Whether "now" falls inside the banner's schedule -- computed once when the banner loads,
  // not from a live Date.now() read during render (impure -- see react-hooks/purity), and not
  // worth a ticking timer for a same-session banner nobody expects to appear mid-visit.
  const [withinSchedule, setWithinSchedule] = useState(false);

  useEffect(() => {
    async function load() {
      const snap = await getDoc(doc(db, "siteContent", "announcementBanner"));
      if (!snap.exists()) return;
      const data = snap.data() as AnnouncementBanner;
      setBanner(data);
      const now = Date.now();
      setWithinSchedule((!data.startsAt || now >= data.startsAt) && (!data.endsAt || now <= data.endsAt));
    }
    void load();
  }, []);

  if (!banner || dismissed || !banner.enabled || !withinSchedule) return null;
  if (banner.scope === "home_only" && pathname !== "/") return null;

  function dismiss() {
    setDismissed(true);
    try {
      sessionStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // Nothing to persist across reloads if storage is unavailable -- not a failure.
    }
  }

  return (
    <div className="flex items-center justify-center gap-3 bg-accent px-4 py-2.5 text-center text-sm text-accent-foreground">
      <span>
        {banner.text}
        {banner.linkUrl && banner.linkLabel && (
          <a href={banner.linkUrl} className="ml-2 underline underline-offset-2">
            {banner.linkLabel}
          </a>
        )}
      </span>
      <button onClick={dismiss} aria-label="Dismiss" className="text-accent-foreground/80 hover:text-accent-foreground">
        ✕
      </button>
    </div>
  );
}
